import { Chip, Typography, Box } from '@mui/material'
import { string, func, object, bool } from 'prop-types'

export default function TopicTag({
  testId,
  label,
  onClick,
  href,
  onDelete,
  deleteIcon,
  variant,
  premium,
  textTransform,
  marginRight,
  color,
}) {
  switch (variant) {
    case 'special':
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            maxWidth: '100%',
          }}
          data-testid={`${testId}-special`}
        >
          {premium && (
            <Box
              component='img'
              role='img'
              src='/images/premium.png'
              alt='premium resources logo'
              style={{ width: '20px', marginRight: '8px' }}
              data-testid={`${testId}-special-premium-img`}
            />
          )}
          <Chip
            component='a'
            data-testid={`${testId}-special-tag`}
            label={
              <Typography
                variant='overline'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {label}
              </Typography>
            }
            href={href}
            onClick={onClick || null}
            onDelete={onDelete || null}
            deleteIcon={deleteIcon}
            sx={{
              mt: 1,
              ml: '2px',
              maxWidth: '100%',
              textTransform: textTransform,
              marginRight: marginRight ? marginRight : 0,
              borderRadius: '4px',
              background: 'transparent',
              backgroundColor:
                color === 'white'
                  ? 'rgba(0, 0, 0, 0.4)'
                  : 'rgba(0, 0, 0, 0.05)',
              color: color ? color : 'text.primary',
              height: '24px',
              mb: 1,
              cursor: 'pointer',
              overflow: 'hidden',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              },
              '&:hover': {
                textDecoration: 'none',
                bgcolor: 'rgba(0, 0, 0, 0.12)',
              },
            }}
            size='small'
          />
        </Box>
      )
    case 'white':
      return (
        <Chip
          data-testid={`${testId}-white`}
          component='a'
          label={
            <Typography
              variant='overline'
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {label}
            </Typography>
          }
          href={href}
          onClick={onClick || null}
          onDelete={onDelete || null}
          deleteIcon={deleteIcon}
          sx={{
            maxWidth: '100%',
            textTransform: textTransform,
            marginRight: marginRight ? marginRight : 0,
            borderRadius: '4px',
            backgroundColor: 'background.light',
            color: 'text.primary',
            cursor: 'pointer',
            overflow: 'hidden',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
            '&:hover': {
              textDecoration: 'none',
              bgcolor: 'rgba(0, 0, 0, 0.12)',
            },
          }}
        />
      )
    case 'premium':
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
          data-testid={`${testId}-premium`}
        >
          <Box
            component='img'
            role='img'
            src='/images/premium.png'
            alt='premium resources logo'
            style={{ width: '30px', marginRight: '8px' }}
            data-testid={`${testId}-prem-premium-img`}
          />
          <Chip
            data-testid={`${testId}-premium-chip`}
            label={
              <Typography
                sx={{
                  textTransform: textTransform,
                  color: '#A45E0A',
                  fontSize: (theme) => theme.typography.pxToRem(11),
                  fontWeight: '600',
                  lineHeight: (theme) => theme.typography.pxToRem(20),
                  letterSpacing: '0.2px',
                  width: '100%',
                }}
              >
                {label}
              </Typography>
            }
            sx={{
              maxWidth: '100%',
              marginRight: marginRight ? marginRight : 0,
              backgroundColor: 'rgba(255, 140, 0, 0.12)',
              color: '#A45E0A',
              fontSize: (theme) => theme.typography.pxToRem(11),
              fontWeight: '600',
              lineHeight: (theme) => theme.typography.pxToRem(20),
              letterSpacing: '0.2px',
            }}
          />
        </Box>
      )
    case 'basic':
      return (
        <Chip
          component='a'
          label={label}
          data-testid={`${testId}-basic-topic`}
          href={href}
          onClick={onClick || null}
          onDelete={onDelete || null}
          deleteIcon={deleteIcon}
          sx={{
            maxWidth: '100%',
            textTransform: textTransform,
            marginRight: marginRight ? marginRight : 0,
            fontSize: (theme) => theme.typography.pxToRem(14),
            fontWeight: '500',
            lineHeight: (theme) => theme.typography.pxToRem(20),
            letterSpacing: '4%',
            cursor: 'pointer',
            overflow: 'hidden',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
            '&:hover': {
              textDecoration: 'none',
              bgcolor: 'rgba(0, 0, 0, 0.12)',
            },
          }}
          color='primary'
          size='medium'
        />
      )
    case 'basicSmall':
      return (
        <Chip
          data-testid={`${testId}-basic-small`}
          component='a'
          label={label}
          href={href}
          onClick={onClick || null}
          onDelete={onDelete || null}
          deleteIcon={deleteIcon}
          sx={{
            maxWidth: '100%',
            textTransform: textTransform,
            marginRight: marginRight ? marginRight : 0,
            fontSize: (theme) => theme.typography.pxToRem(11),
            fontWeight: '500',
            lineHeight: (theme) => theme.typography.pxToRem(20),
            letterSpacing: '4%',
            cursor: 'pointer',
            overflow: 'hidden',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
            '&:hover': {
              textDecoration: 'none',
              bgcolor: 'rgba(0, 0, 0, 0.12)',
            },
          }}
          color='primary'
          size='medium'
        />
      )
    default:
      return (
        <Chip
          data-testid={`${testId}-default`}
          component='a'
          label={label}
          href={href}
          onClick={onClick || null}
          onDelete={onDelete || null}
          deleteIcon={deleteIcon}
          sx={{
            maxWidth: '100%',
            textTransform: textTransform,
            marginRight: marginRight ? marginRight : 0,
            fontSize: (theme) => theme.typography.pxToRem(14),
            fontWeight: '500',
            lineHeight: (theme) => theme.typography.pxToRem(20),
            letterSpacing: '4%',
            cursor: 'pointer',
            overflow: 'hidden',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
            '&:hover': {
              textDecoration: 'none',
              bgcolor: 'rgba(0, 0, 0, 0.12)',
            },
          }}
          color={variant === 'basic' ? 'primary' : 'secondary'}
          size='small'
        />
      )
  }
}

TopicTag.propTypes = {
  testId: string,
  label: string,
  href: string,
  onClick: func,
  onDelete: func,
  deleteIcon: object,
  variant: string,
  premium: bool,
  textTransform: string,
  marginRight: string,
  color: string,
}
